import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, FileText, GitBranch, AlertTriangle, TrendingUp,
  CheckCircle, XCircle, Clock, AlertOctagon, RefreshCw,
  Search, Ban, Pause, Eye, ArrowRight, BarChart2,
  Shield, Target, Activity, Calendar
} from 'lucide-react';

// Colores para estados
const ESTADO_COLORS = {
  'ACTIVO': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  'DADO_DE_BAJA': { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle },
  'EN_INVESTIGACION': { bg: 'bg-amber-100', text: 'text-amber-700', icon: Search },
  'SUSPENDIDO': { bg: 'bg-red-100', text: 'text-red-700', icon: Pause },
  'BLOQUEADO': { bg: 'bg-red-100', text: 'text-red-700', icon: Ban },
  'OBSERVADO': { bg: 'bg-purple-100', text: 'text-purple-700', icon: Eye }
};

const KPI_ICONS = {
  'cumplimiento_sla': Clock,
  'cobertura_documental': FileText,
  'clientes_riesgo': AlertTriangle,
  'tiempo_resolucion_casos_dias': Activity,
  'tasa_atencion_alertas': CheckCircle
};

const ComplianceDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setRefreshing(true);
      const response = await api.get('/compliance/dashboard');
      setDashboard(response.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getKPIColor = (estado) => {
    switch(estado) {
      case 'ok': return 'text-green-600';
      case 'warning': return 'text-amber-600';
      case 'critical': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };

  const getKPIBg = (estado) => {
    switch(estado) {
      case 'ok': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={48} />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12 text-slate-500">
        Error al cargar el dashboard
      </div>
    );
  }

  const { clientes, legajos, aprobaciones, alertas, kpis } = dashboard;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="text-indigo-600" size={28} />
            Dashboard de Compliance
          </h1>
          <p className="text-slate-500 mt-1">
            Vista ejecutiva del estado de compliance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">
            Actualizado: {formatDate(dashboard.generated_at)}
          </span>
          <Button 
            variant="outline" 
            onClick={loadDashboard}
            disabled={refreshing}
          >
            <RefreshCw className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} size={16} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(kpis).filter(([key]) => key !== 'periodo').map(([key, kpi]) => {
          const Icon = KPI_ICONS[key] || Target;
          const label = {
            'cumplimiento_sla': 'Cumplimiento SLA',
            'cobertura_documental': 'Cobertura Documental',
            'clientes_riesgo': 'Clientes en Riesgo',
            'tiempo_resolucion_casos_dias': 'Tiempo Resolución (días)',
            'tasa_atencion_alertas': 'Atención Alertas'
          }[key] || key;
          
          const isInverse = key === 'clientes_riesgo' || key === 'tiempo_resolucion_casos_dias';
          
          return (
            <Card 
              key={key} 
              className={`p-4 border ${getKPIBg(kpi.estado)}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                  <p className={`text-2xl font-bold mt-1 ${getKPIColor(kpi.estado)}`}>
                    {kpi.valor}{key.includes('tasa') || key.includes('cumplimiento') || key.includes('cobertura') || key.includes('clientes') ? '%' : ''}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Objetivo: {isInverse ? '≤' : '≥'} {kpi.objetivo}{key.includes('tasa') || key.includes('cumplimiento') || key.includes('cobertura') || key.includes('clientes') ? '%' : ' días'}
                  </p>
                </div>
                <Icon className={getKPIColor(kpi.estado)} size={24} />
              </div>
              <Progress 
                value={isInverse ? Math.max(0, 100 - (kpi.valor / kpi.objetivo * 50)) : Math.min(100, kpi.valor / kpi.objetivo * 100)} 
                className="mt-3 h-1"
              />
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estados de Clientes */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="text-indigo-600" size={20} />
              Estados de Clientes
            </h2>
            <Badge variant="outline">{clientes.total} total</Badge>
          </div>
          
          {/* Mini gráfico de barras */}
          <div className="space-y-2 mb-4">
            {Object.entries(clientes.por_estado).map(([estado, count]) => {
              const config = ESTADO_COLORS[estado] || { bg: 'bg-slate-100', text: 'text-slate-700' };
              const percentage = (count / clientes.total * 100).toFixed(1);
              return (
                <div key={estado} className="flex items-center gap-3">
                  <span className={`text-xs font-medium w-32 ${config.text}`}>{estado}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${config.bg.replace('100', '500')}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-16 text-right">{count} ({percentage}%)</span>
                </div>
              );
            })}
          </div>
          
          {/* Clientes críticos */}
          {clientes.clientes_criticos?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                <AlertOctagon size={14} />
                Clientes Críticos ({clientes.clientes_criticos.length})
              </p>
              <div className="space-y-2 max-h-32 overflow-auto">
                {clientes.clientes_criticos.slice(0, 5).map((c, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="truncate max-w-[200px]">{c.razon_social || c.cuit_limpio}</span>
                    <Badge variant="destructive" className="text-xs">
                      {c.dias_en_estado}d en {c.estado_cliente}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Aprobaciones Pendientes */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <GitBranch className="text-blue-600" size={20} />
              Flujos de Aprobación
            </h2>
            <div className="flex gap-2">
              {aprobaciones.vencidas > 0 && (
                <Badge variant="destructive">{aprobaciones.vencidas} vencidas</Badge>
              )}
              {aprobaciones.por_vencer > 0 && (
                <Badge className="bg-amber-100 text-amber-700">{aprobaciones.por_vencer} por vencer</Badge>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{aprobaciones.flujos_activos}</p>
              <p className="text-xs text-slate-500">Flujos Activos</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{aprobaciones.total_pendientes}</p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{aprobaciones.tiempo_promedio_resolucion_horas}h</p>
              <p className="text-xs text-slate-500">Tiempo Prom.</p>
            </div>
          </div>
          
          {/* Lista de pendientes */}
          {aprobaciones.solicitudes_pendientes?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Pendientes más urgentes</p>
              <div className="space-y-2 max-h-32 overflow-auto">
                {aprobaciones.solicitudes_pendientes.slice(0, 5).map((sol, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="truncate max-w-[200px]">{sol.entity_nombre}</span>
                    <Badge 
                      variant={sol.horas_restantes <= 0 ? 'destructive' : sol.horas_restantes <= 4 ? 'warning' : 'secondary'}
                      className="text-xs"
                    >
                      {sol.horas_restantes <= 0 ? 'Vencido' : `${sol.horas_restantes}h`}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Legajos de Documentos */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="text-teal-600" size={20} />
              Legajos de Documentos
            </h2>
            <Badge variant="outline">{legajos.total_documentos} docs</Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-teal-50 rounded-lg">
              <p className="text-2xl font-bold text-teal-600">{legajos.porcentaje_cobertura}%</p>
              <p className="text-xs text-slate-500">Cobertura</p>
              <p className="text-xs text-slate-400">{legajos.clientes_con_documentos} clientes con docs</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{legajos.clientes_sin_documentos}</p>
              <p className="text-xs text-slate-500">Sin Documentos</p>
              <p className="text-xs text-slate-400">{legajos.documentos_subidos_hoy} subidos hoy</p>
            </div>
          </div>
          
          {/* Documentos por categoría */}
          <div className="space-y-1">
            {Object.entries(legajos.documentos_por_categoria || {}).slice(0, 4).map(([cat, count]) => (
              <div key={cat} className="flex items-center justify-between text-sm">
                <span className="text-slate-600 capitalize">{cat}</span>
                <span className="text-slate-900 font-medium">{count}</span>
              </div>
            ))}
          </div>
          
          {/* Clientes con docs incompletos */}
          {legajos.clientes_docs_incompletos?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-amber-600 mb-2">Legajos Incompletos</p>
              <div className="space-y-2 max-h-24 overflow-auto">
                {legajos.clientes_docs_incompletos.slice(0, 3).map((c, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="truncate">{c.razon_social || c.cuit}</span>
                    <span className="text-xs text-slate-400 ml-2">
                      Falta: {c.categorias_faltantes?.join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Alertas */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={20} />
              Alertas Activas
            </h2>
            <Badge variant={alertas.criticas > 0 ? 'destructive' : 'secondary'}>
              {alertas.total_activas} activas
            </Badge>
          </div>
          
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="text-center p-2 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600">{alertas.criticas}</p>
              <p className="text-xs text-slate-500">Críticas</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg">
              <p className="text-xl font-bold text-amber-600">{alertas.altas}</p>
              <p className="text-xs text-slate-500">Altas</p>
            </div>
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xl font-bold text-slate-600">{alertas.sin_atender_48h}</p>
              <p className="text-xs text-slate-500">&gt;48h</p>
            </div>
            <div className="text-center p-2 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-600">{alertas.alertas_hoy}</p>
              <p className="text-xs text-slate-500">Hoy</p>
            </div>
          </div>
          
          {/* Alertas más antiguas */}
          {alertas.alertas_antiguas?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Más antiguas sin resolver</p>
              <div className="space-y-2 max-h-32 overflow-auto">
                {alertas.alertas_antiguas.map((a, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="truncate max-w-[200px]">{a.titulo}</span>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={a.severidad === 'CRITICA' ? 'destructive' : a.severidad === 'ALTA' ? 'warning' : 'secondary'}
                        className="text-xs"
                      >
                        {a.severidad}
                      </Badge>
                      <span className="text-xs text-slate-400">{a.dias_antiguedad}d</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ComplianceDashboard;
